import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CalendarIcon, UserIcon, TagIcon, MessageSquareIcon, EyeIcon, FolderIcon, ShareIcon, ClockIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getArticleById, getArticleComments, addComment, incrementViewCount, trackArticleEngagement, Article, Comment } from '../services/articles';
import LoginButton from '../components/auth/LoginButton';
import SocialShareButtons from '../components/blog/SocialShareButtons';
import EmailSubscribe from '../components/blog/EmailSubscribe';
import RelatedArticles from '../components/blog/RelatedArticles';
const ArticlePage: React.FC = () => {
  const {
    articleId
  } = useParams<{
    articleId: string;
  }>();
  const {
    user
  } = useAuth();
  const [article, setArticle] = useState<Article | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commentContent, setCommentContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [commentSubmitted, setCommentSubmitted] = useState(false);
  const [viewCount, setViewCount] = useState<number>(0);
  useEffect(() => {
    const fetchArticleAndComments = async () => {
      if (!articleId) return;
      try {
        const fetchedArticle = await getArticleById(articleId);
        if (fetchedArticle) {
          setArticle(fetchedArticle);
          const fetchedComments = await getArticleComments(articleId);
          setComments(fetchedComments);
          // Increment view count and track view
          const newViewCount = await incrementViewCount(articleId);
          setViewCount(newViewCount);
          // Track article read engagement after 30 seconds
          const readTimer = setTimeout(() => {
            trackArticleEngagement(articleId, 'read', {
              time_spent: 30,
              read_percentage: 50 // estimated percentage
            });
          }, 30000);
          return () => clearTimeout(readTimer);
        } else {
          setError('Article not found');
        }
      } catch (err) {
        setError('Failed to load article. Please try again later.');
        console.error('Error fetching article:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticleAndComments();
  }, [articleId]);
  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !articleId || !commentContent.trim()) return;
    setSubmitting(true);
    try {
      await addComment(articleId, commentContent, user);
      setCommentContent('');
      setCommentSubmitted(true);
      // Refresh comments if user has appropriate role
      if (user.role !== 'pending') {
        const updatedComments = await getArticleComments(articleId);
        setComments(updatedComments);
      }
      setTimeout(() => {
        setCommentSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('Error submitting comment:', err);
    } finally {
      setSubmitting(false);
    }
  };
  const handleShare = (platform: string) => {
    if (article && articleId) {
      trackArticleEngagement(articleId, 'share', {
        platform
      });
    }
  };
  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Date unavailable';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  if (loading) {
    return <div className="container-custom py-16 flex justify-center" data-id="element-802">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-navy" data-id="element-803"></div>
      </div>;
  }
  if (error || !article) {
    return <div className="container-custom py-16 text-center" data-id="element-804">
        <h1 className="text-2xl font-bold text-navy mb-4" data-id="element-805">Error</h1>
        <p className="text-lg" data-id="element-806">{error || 'Article not found'}</p>
        <Link to="/enews" className="mt-6 inline-block text-navy hover:text-orange" data-id="element-807">
          ← Back to eNews
        </Link>
      </div>;
  }
  return <div className="w-full bg-white" data-id="element-808">
      <article className="container-custom py-12" data-id="element-809">
        {/* Breadcrumb */}
        <div className="mb-8" data-id="element-810">
          <nav className="flex" aria-label="Breadcrumb" data-id="element-811">
            <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-812">
              <li className="inline-flex items-center" data-id="element-813">
                <Link to="/" className="text-gray-600 hover:text-orange" data-id="element-814">
                  Home
                </Link>
              </li>
              <li data-id="element-815">
                <div className="flex items-center" data-id="element-816">
                  <span className="mx-2 text-gray-400" data-id="element-817">/</span>
                  <Link to="/enews" className="text-gray-600 hover:text-orange" data-id="element-818">
                    eNews
                  </Link>
                </div>
              </li>
              {article.categoryName && <li data-id="element-819">
                  <div className="flex items-center" data-id="element-820">
                    <span className="mx-2 text-gray-400" data-id="element-821">/</span>
                    <Link to={`/enews/category/${article.categoryId}`} className="text-gray-600 hover:text-orange" data-id="element-822">
                      {article.categoryName}
                    </Link>
                  </div>
                </li>}
              <li aria-current="page" data-id="element-823">
                <div className="flex items-center" data-id="element-824">
                  <span className="mx-2 text-gray-400" data-id="element-825">/</span>
                  <span className="text-gray-500 truncate max-w-xs" data-id="element-826">
                    {article.title}
                  </span>
                </div>
              </li>
            </ol>
          </nav>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-827">
          <div className="lg:col-span-2" data-id="element-828">
            {/* Article Header */}
            <header className="mb-8" data-id="element-829">
              <h1 className="text-3xl md:text-4xl font-bold text-navy mb-6 font-montserrat" data-id="element-830">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center text-gray-600 gap-4 mb-6" data-id="element-831">
                <div className="flex items-center" data-id="element-832">
                  <CalendarIcon size={18} className="mr-2" data-id="element-833" />
                  <span data-id="element-834">{formatDate(article.publishedAt)}</span>
                </div>
                <div className="flex items-center" data-id="element-835">
                  <UserIcon size={18} className="mr-2" data-id="element-836" />
                  <span data-id="element-837">{article.author.name}</span>
                </div>
                {article.categoryName && <div className="flex items-center" data-id="element-838">
                    <FolderIcon size={18} className="mr-2" data-id="element-839" />
                    <Link to={`/enews/category/${article.categoryId}`} className="hover:text-orange transition duration-300" data-id="element-840">
                      {article.categoryName}
                    </Link>
                  </div>}
                <div className="flex items-center" data-id="element-841">
                  <ClockIcon size={18} className="mr-2" data-id="element-842" />
                  <span data-id="element-843">{article.readTime || 3} min read</span>
                </div>
                <div className="flex items-center" data-id="element-844">
                  <EyeIcon size={18} className="mr-2" data-id="element-845" />
                  <span data-id="element-846">{viewCount || article.viewCount || 0} views</span>
                </div>
                <div className="flex items-center" data-id="element-847">
                  <MessageSquareIcon size={18} className="mr-2" data-id="element-848" />
                  <span data-id="element-849">{comments.length} comments</span>
                </div>
              </div>
              {article.tags && article.tags.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-850">
                  {article.tags.map((tag, index) => <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-851">
                      <TagIcon size={14} className="mr-1" data-id="element-852" />
                      {tag}
                    </span>)}
                </div>}
            </header>

            {/* Featured Image */}
            {article.featuredImage && <div className="mb-8" data-id="element-853">
                <img src={article.featuredImage} alt={article.title} className="w-full h-auto rounded-lg shadow-md" data-id="element-854" />
              </div>}

            {/* Social Share */}
            <div className="mb-8 flex items-center" data-id="element-855">
              <span className="mr-3 font-medium flex items-center" data-id="element-856">
                <ShareIcon size={18} className="mr-2" data-id="element-857" />
                Share:
              </span>
              <SocialShareButtons url={window.location.href} title={article.title} excerpt={article.excerpt} onShare={handleShare} data-id="element-858" />
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none mb-12" data-id="element-859">
              <div dangerouslySetInnerHTML={{
              __html: article.content
            }} data-id="element-860" />
            </div>

            {/* Author Bio */}
            <div className="bg-gray-50 rounded-lg p-6 mb-12" data-id="element-861">
              <div className="flex items-start" data-id="element-862">
                {article.author.photoURL ? <img src={article.author.photoURL} alt={article.author.name} className="w-16 h-16 rounded-full mr-4" data-id="element-863" /> : <div className="w-16 h-16 rounded-full bg-navy text-white flex items-center justify-center mr-4 text-xl" data-id="element-864">
                    {article.author.name.charAt(0).toUpperCase()}
                  </div>}
                <div data-id="element-865">
                  <h3 className="text-lg font-bold text-navy mb-2" data-id="element-866">
                    About {article.author.name}
                  </h3>
                  <p className="text-gray-600" data-id="element-867">
                    Content creator at Operation Launchpad. Passionate about
                    helping entrepreneurs succeed.
                  </p>
                </div>
              </div>
            </div>

            {/* Comments Section */}
            <section className="mt-12 pt-8 border-t border-gray-200" data-id="element-868">
              <h2 className="text-2xl font-bold text-navy mb-6 font-montserrat" data-id="element-869">
                Comments ({comments.length})
              </h2>
              {comments.length > 0 ? <div className="space-y-6 mb-8" data-id="element-870">
                  {comments.map(comment => <div key={comment.id} className="bg-gray-50 p-4 rounded-lg" data-id="element-871">
                      <div className="flex items-start" data-id="element-872">
                        {comment.author.photoURL ? <img src={comment.author.photoURL} alt={comment.author.name} className="w-10 h-10 rounded-full mr-4" data-id="element-873" /> : <div className="w-10 h-10 rounded-full bg-navy text-white flex items-center justify-center mr-4" data-id="element-874">
                            {comment.author.name.charAt(0).toUpperCase()}
                          </div>}
                        <div className="flex-1" data-id="element-875">
                          <div className="flex justify-between" data-id="element-876">
                            <h4 className="font-bold" data-id="element-877">{comment.author.name}</h4>
                            <span className="text-sm text-gray-500" data-id="element-878">
                              {formatDate(comment.createdAt)}
                            </span>
                          </div>
                          <p className="mt-2" data-id="element-879">{comment.content}</p>
                        </div>
                      </div>
                    </div>)}
                </div> : <p className="text-gray-500 mb-8" data-id="element-880">
                  No comments yet. Be the first to comment!
                </p>}

              {/* Comment Form */}
              <div className="bg-gray-50 p-6 rounded-lg" data-id="element-881">
                <h3 className="text-xl font-bold text-navy mb-4 font-montserrat" data-id="element-882">
                  Leave a Comment
                </h3>
                {!user ? <div className="text-center py-4" data-id="element-883">
                    <p className="mb-4" data-id="element-884">Please sign in to leave a comment</p>
                    <LoginButton data-id="element-885" />
                  </div> : <form onSubmit={handleCommentSubmit} data-id="element-886">
                    <div className="mb-4" data-id="element-887">
                      <label htmlFor="comment" className="block mb-2 font-medium" data-id="element-888">
                        Your Comment
                      </label>
                      <textarea id="comment" rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" value={commentContent} onChange={e => setCommentContent(e.target.value)} required data-id="element-889"></textarea>
                    </div>
                    {user.role === 'pending' && <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-md" data-id="element-890">
                        Your comment will be reviewed by a moderator before
                        appearing.
                      </div>}
                    {commentSubmitted && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md" data-id="element-891">
                        {user.role === 'pending' ? 'Your comment has been submitted and is awaiting approval.' : 'Your comment has been posted successfully.'}
                      </div>}
                    <button type="submit" className="btn-primary" disabled={submitting || !commentContent.trim()} data-id="element-892">
                      {submitting ? 'Submitting...' : 'Post Comment'}
                    </button>
                  </form>}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1" data-id="element-893">
            <div className="sticky top-24" data-id="element-894">
              <EmailSubscribe data-id="element-895" />
              {/* Related Articles */}
              <div className="mt-8" data-id="element-896">
                <RelatedArticles article={article} limit={3} data-id="element-897" />
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>;
};
export default ArticlePage;